import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import type { ResultItem, ResultStatus } from "./ResultSection";

interface ResultActionsProps {
  system: "카드" | "뉴딜";
  headline: string;
  items: ResultItem[];
}

const statusLabel: Record<ResultStatus, string> = {
  pass: "통과",
  block: "제한",
  conditional: "확인 필요",
  info: "참고",
};

const statusColor: Record<ResultStatus, string> = {
  pass: "#16a34a",
  block: "#dc2626",
  conditional: "#d97706",
  info: "#6b7280",
};

const FONT = "'Apple SD Gothic Neo', 'Malgun Gothic', sans-serif";

const focusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-brand-400 dark:focus-visible:ring-offset-gray-900";

function buildSummary(system: "카드" | "뉴딜", headline: string, items: ResultItem[]): string {
  const title = system === "카드" ? "국민내일배움카드" : "K-뉴딜 아카데미";
  const lines = [`국비 자격 자가진단 — ${title}`, headline, ""];
  items.forEach((i) => {
    lines.push(`[${statusLabel[i.status]}] ${i.label}: ${i.message}`);
    if (i.resolve) lines.push(`  → ${i.resolve}`);
  });
  return lines.join("\n");
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  let line = "";
  for (const ch of text) {
    const test = line + ch;
    if (ctx.measureText(test).width > maxWidth && line !== "") {
      lines.push(line);
      line = ch;
    } else {
      line = test;
    }
  }
  if (line !== "") lines.push(line);
  return lines;
}

function renderResultImage(
  system: "카드" | "뉴딜",
  headline: string,
  items: ResultItem[]
): string {
  const W = 1080;
  const padX = 56;
  const padTop = 48;
  const contentW = W - padX * 2;

  const cv = document.createElement("canvas");
  const measure = cv.getContext("2d");
  if (!measure) return "";

  interface Op {
    text: string;
    x: number;
    y: number;
    font: string;
    color: string;
  }
  const ops: Op[] = [];
  let y = padTop;

  const addBlock = (
    text: string,
    font: string,
    color: string,
    lineHeight: number,
    gapBefore = 0
  ) => {
    y += gapBefore;
    measure.font = font;
    const lines = wrapText(measure, text, contentW);
    lines.forEach((ln, idx) => {
      ops.push({ text: ln, x: padX, y: y + lineHeight * (idx + 1), font, color });
    });
    y += lineHeight * lines.length;
  };

  const title = system === "카드" ? "국민내일배움카드" : "K-뉴딜 아카데미";

  addBlock("국비 자격 자가진단", `bold 42px ${FONT}`, "#111827", 54);
  addBlock(title, `20px ${FONT}`, "#6b7280", 28, 8);
  addBlock(headline, `bold 30px ${FONT}`, "#111827", 42, 22);

  items.forEach((item) => {
    const color = statusColor[item.status];
    addBlock(
      `${statusLabel[item.status]}  ·  ${item.label}`,
      `bold 24px ${FONT}`,
      "#111827",
      32,
      16
    );
    addBlock(item.message, `21px ${FONT}`, "#374151", 30, 2);
    if (item.resolve) {
      addBlock(`→ ${item.resolve}`, `18px ${FONT}`, color, 26, 4);
    }
  });

  addBlock(
    "이 판정은 제도 문언에 비춘 가능성입니다. 확정은 상담·운영기관 확인이 필요합니다.",
    `15px ${FONT}`,
    "#9ca3af",
    22,
    26
  );

  const totalH = y + padTop;

  cv.width = W;
  cv.height = totalH;
  const ctx = cv.getContext("2d");
  if (!ctx) return "";

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, W, totalH);

  ops.forEach((op) => {
    ctx.font = op.font;
    ctx.fillStyle = op.color;
    ctx.fillText(op.text, op.x, op.y);
  });

  return cv.toDataURL("image/png");
}

export default function ResultActions({ system, headline, items }: ResultActionsProps) {
  const [feedback, setFeedback] = useState("");

  const blockedItems = items.filter(
    (i) => i.status === "block" || i.status === "conditional"
  );

  const notify = useCallback((msg: string) => {
    setFeedback(msg);
    window.setTimeout(() => setFeedback(""), 2200);
  }, []);

  const copyText = useCallback(async () => {
    const text = buildSummary(system, headline, items);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    notify("텍스트를 복사했습니다");
  }, [system, headline, items, notify]);

  const handleShare = useCallback(async () => {
    const text = buildSummary(system, headline, items);
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({
          title: "국비 자격 자가진단 결과",
          text,
        });
        return;
      } catch {
        // 사용자가 공유를 취소한 경우 조용히 무시
      }
    }
    await copyText();
  }, [system, headline, items, copyText]);

  const handleImage = useCallback(() => {
    try {
      const url = renderResultImage(system, headline, items);
      if (!url) {
        notify("이미지를 만들지 못했습니다");
        return;
      }
      const a = document.createElement("a");
      a.href = url;
      a.download = "국비-자가진단-결과.png";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      notify("이미지를 저장했습니다");
    } catch {
      notify("이미지를 만들지 못했습니다");
    }
  }, [system, headline, items, notify]);

  const counselHref = `/?axis=${system}&items=${encodeURIComponent(
    blockedItems.map((i) => i.label).join(",")
  )}#문의`;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
      <div className="mb-3 flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300">
          <i className="ri-save-line text-sm" aria-hidden="true" />
        </span>
        <p className="text-sm font-bold text-gray-900 dark:text-gray-100">결과 저장·공유</p>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={handleShare}
          className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-bold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800 ${focusRing}`}
        >
          <i className="ri-share-forward-line text-base" />
          공유하기
        </button>
        <button
          type="button"
          onClick={copyText}
          className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-bold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800 ${focusRing}`}
        >
          <i className="ri-file-copy-line text-base" />
          텍스트 복사
        </button>
        <button
          type="button"
          onClick={handleImage}
          className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-bold text-white bg-brand-700 hover:bg-brand-800 transition-colors cursor-pointer whitespace-nowrap ${focusRing}`}
        >
          <i className="ri-download-2-line text-base" />
          이미지 저장
        </button>
      </div>

      {blockedItems.length > 0 && (
        <Link
          to={counselHref}
          className={`mt-3 flex w-full items-center justify-center gap-2 rounded-md bg-brand-700 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-800 ${focusRing}`}
        >
          <i className="ri-customer-service-2-line text-base" />
          상담 문의하기
        </Link>
      )}

      {feedback && (
        <p className="text-xs text-brand-700 mt-2 break-keep dark:text-brand-400">{feedback}</p>
      )}
    </div>
  );
}